class CustomProductAttributes extends HTMLElement {
  constructor() {
    super();

    this.form = null;
    this.addToCartButton = null;
    this.originalButtonText = null;
    this.originalDisabledState = null;
    this.optionSelectors = [];
    this.requiredSelections = new Set();
    this.completedSelections = new Map();
    this.isCombinedListing = false;

    this.init();
  }

  init() {
    this.isCombinedListing = this.dataset.combinedListing === 'true';
    this.refreshActiveSelectors();

    const productSection = this.closest('.product');
    if (!productSection) {
      console.warn('Custom product attributes: Product section not found');
      return;
    }

    productSection.addEventListener('on:variant:change', this.handleVariantChange.bind(this));
    this.findForm();
  }

  refreshActiveSelectors() {
    if (this.isCombinedListing) {
      const activeChild = this.querySelector('.custom-attributes__child:not([hidden])');
      this.optionSelectors = activeChild ? [...activeChild.querySelectorAll('.option-selector')] : [];
    } else {
      this.optionSelectors = [...this.querySelectorAll('.option-selector')];
    }

    this.requiredSelections = new Set(
      this.optionSelectors.map((selector) => {
        const radio = selector.querySelector('input[type="radio"]');
        const select = selector.querySelector('select');
        return radio?.name || select?.name;
      })
    );

    this.completedSelections = new Map();
  }

  findForm() {
    const productForm = document.querySelector(`#${this.dataset.productForm}`);
    if (!productForm) {
      requestAnimationFrame(() => this.findForm());
      return;
    }

    this.form = productForm;
    this.addToCartButton = this.form?.querySelector('[name="add"]')
      || document.querySelector('[data-enquire-now-button]');

    if (!this.form || !this.addToCartButton) {
      console.warn('Custom product attributes: Form elements not found');
      return;
    }

    this.originalButtonText = this.addToCartButton.dataset.addToCartText
      || this.addToCartButton.dataset.enquireText
      || this.addToCartButton.textContent;
    this.originalDisabledState = this.addToCartButton.disabled;

    this.addToCartButton.setAttribute('aria-disabled', 'true');
    this.addToCartButton.disabled = true;
    this.addToCartButton.textContent = window.theme?.strings?.selectOptions || 'Please select all options';

    this.setupEventListeners();
  }

  handleVariantChange(event) {
    if (event.detail.variant) {
      this.originalButtonText = this.addToCartButton.dataset.addToCartText || this.addToCartButton.textContent;
      this.originalDisabledState = !event.detail.variant.available;

      if (this.isCombinedListing) {
        this.swapChildAttributes(event.detail.variant.id);
      }

      this.updateAddToCartButton();
    }
  }

  swapChildAttributes(variantId) {
    const variantIdStr = variantId.toString();
    const children = this.querySelectorAll('.custom-attributes__child');

    children.forEach((child) => {
      child.hidden = child.dataset.childVariantId !== variantIdStr;
    });

    this.clearPropertyInputs();
    this.refreshActiveSelectors();
    this.setupEventListeners();
  }

  clearPropertyInputs() {
    if (!this.form) return;

    this.querySelectorAll('.option-selector legend, .option-selector > label').forEach((el) => {
      const label = el.textContent.trim();
      const input = this.form.querySelector(`input[name="properties[${label}]"]`);
      if (input) input.remove();
    });
  }

  setupEventListeners() {
    this.optionSelectors.forEach((selector) => {
      if (selector.dataset.listenersAttached) return;
      selector.dataset.listenersAttached = 'true';

      const {selectorType} = selector.dataset;

      if (selectorType === 'dropdown') {
        const nativeSelect = selector.querySelector('select');
        if (!nativeSelect) return;

        nativeSelect.addEventListener('change', () => this.onSelectChange(nativeSelect));
        return;
      }

      if (selectorType === 'radio') {
        const radioButtons = selector.querySelectorAll('input[type="radio"]');
        if (!radioButtons.length) return;

        radioButtons.forEach((radio) => {
          radio.addEventListener('change', () => this.onOptionChange(radio));
        });
        return;
      }
    });
  }

  onSelectChange(select) {
    const legend = select.closest('.option-selector').querySelector('label').textContent;
    const {value} = select;

    this.updateHiddenInput(legend, value);
    this.updateAddToCartButton();

    this.dispatchEvent(
      new CustomEvent('custom-attribute:change', {
        bubbles: true,
        detail: {
          optionId: select.id,
          value,
          propertyName: legend,
        },
      })
    );
  }

  onOptionChange(radio) {
    const optionId = radio.name.split('-')[0];
    const {value} = radio;
    const legend = radio.closest('fieldset').querySelector('legend').textContent;

    const fieldset = radio.closest('fieldset');
    fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
      input.checked = input == radio;
    });

    this.updateHiddenInput(legend, value);
    this.updateAddToCartButton();

    this.dispatchEvent(
      new CustomEvent('custom-attribute:change', {
        bubbles: true,
        detail: {
          optionId,
          value,
          propertyName: legend,
        },
      })
    );
  }

  updateHiddenInput(legend, value) {
    let hiddenInput = this.form.querySelector(`input[name="properties[${legend}]"]`);
    if (!hiddenInput) {
      hiddenInput = document.createElement('input');
      hiddenInput.type = 'hidden';
      hiddenInput.name = `properties[${legend}]`;
      this.form.appendChild(hiddenInput);
    }
    hiddenInput.value = value;
  }

  updateAddToCartButton() {
    if (!this.addToCartButton) return;

    const allSelectionsComplete = this.checkAllSelectionsComplete();

    const unavailableStrings = [
      window.theme?.strings?.noStock,
      window.theme?.strings?.noVariant,
      window.theme?.strings?.noSelectedVariant,
    ];
    if (unavailableStrings.includes(this.addToCartButton.textContent)) {
      return;
    }

    if (allSelectionsComplete) {
      this.addToCartButton.textContent = this.originalButtonText;
      if (this.originalDisabledState) {
        this.addToCartButton.setAttribute('aria-disabled', 'true');
        this.addToCartButton.disabled = true;
        return;
      }
      this.addToCartButton.removeAttribute('aria-disabled');
      this.addToCartButton.disabled = false;
      return;
    }

    this.addToCartButton.setAttribute('aria-disabled', 'true');
    this.addToCartButton.disabled = true;
    this.addToCartButton.textContent = window.theme?.strings?.selectOptions || 'Please select all options';
  }

  checkAllSelectionsComplete() {
    return this.optionSelectors.every((selector) => {
      if (selector.dataset.selectorType === 'dropdown') {
        const select = selector.querySelector('select');
        return select && select.value !== '';
      } else {
        const checkedInput = selector.querySelector('input[type="radio"]:checked');
        return checkedInput !== null;
      }
    });
  }
}

customElements.define('custom-product-attributes', CustomProductAttributes);
